import {
  loginStudentCourseWithCode,
  loginStudentCourseWithLoginToken,
  shouldRedirectToStudentLogin,
  verifyStudentCourseAuth,
} from '@teachino/library-feature-student-course';

const LOGIN_TOKEN_QUERY_KEY = 'loginToken';
const STUDENT_CODE_QUERY_KEY = 'studentCode';

const omitQueryKeys = (
  query: Record<string, unknown>,
  keys: string[],
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(query).filter(([key]) => !keys.includes(key)),
  );

export default defineNuxtRouteMiddleware(async (to) => {
  const code = to.params.code;
  if (typeof code !== 'string') {
    return;
  }

  const guardArgs = { coursePublicAccessCode: code };
  const redirectToLogin = () => {
    const destinationQuery = omitQueryKeys(to.query, [
      LOGIN_TOKEN_QUERY_KEY,
      STUDENT_CODE_QUERY_KEY,
    ]);
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(destinationQuery)) {
      const values = Array.isArray(value) ? value : [value];
      for (const entry of values) {
        if (typeof entry === 'string') searchParams.append(key, entry);
      }
    }
    const returnTo =
      searchParams.size > 0
        ? `${to.path}?${searchParams.toString()}`
        : to.path;
    return navigateTo(
      {
        path: '/student-login',
        query: { courseCode: code, returnTo },
      },
      { replace: true },
    );
  };
  // Credentials that arrive in the URL are consumed once and then replaced out
  // of the history entry, so they cannot be re-shared or restored via back.
  const redirectWithoutQueryKey = (queryKey: string) => {
    const query = omitQueryKeys(to.query, [queryKey]);
    return navigateTo({ path: to.path, query }, { replace: true });
  };

  const loginToken = to.query[LOGIN_TOKEN_QUERY_KEY];
  if (typeof loginToken === 'string') {
    const loginSucceeded = await loginStudentCourseWithLoginToken({
      token: loginToken,
      expectedCoursePublicAccessCode: code,
    });
    return loginSucceeded
      ? redirectWithoutQueryKey(LOGIN_TOKEN_QUERY_KEY)
      : redirectToLogin();
  }

  const studentCode = to.query[STUDENT_CODE_QUERY_KEY];
  if (typeof studentCode === 'string') {
    const loginSucceeded = await loginStudentCourseWithCode({
      studentCode,
      expectedCoursePublicAccessCode: code,
    });
    return loginSucceeded
      ? redirectWithoutQueryKey(STUDENT_CODE_QUERY_KEY)
      : redirectToLogin();
  }

  if (shouldRedirectToStudentLogin(guardArgs)) {
    return redirectToLogin();
  }

  const isAuthValid = await verifyStudentCourseAuth(guardArgs);
  if (!isAuthValid) {
    return redirectToLogin();
  }
});
